import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import DeleteIcon from '@mui/icons-material/Delete';
// Publish/unpublish is an eye pair — deliberately *not* an arrow, so it cannot
// be confused with the pull-to-top action next to it.
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import BlockIcon from '@mui/icons-material/Block';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import {
    fetchArticles,
    deleteArticle,
    toggleArticleVisibility,
    toggleArticlePullUp,
    selectAllArticles,
    selectArticleStatus,
    selectArticleError,
} from './ArticleSlice';
import { selectArticleTypeDict, selectAllArticleTypes } from './ArticleTypeSlice';

// Delete Confirmation Modal Component
const DeleteConfirmDialog = ({ open, onClose, onConfirm, articleTitle }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>Delete Article</DialogTitle>
        <DialogContent>
            <DialogContentText>Are you sure you want to delete "{articleTitle}"? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} color='primary'>
                Cancel
            </Button>
            <Button onClick={onConfirm} color='error' variant='contained'>
                Delete
            </Button>
        </DialogActions>
    </Dialog>
);

// #region ARTICLES GRID
const ArticlesGrid = ({ onEditArticle }) => {
    const dispatch = useDispatch();
    const articles = useSelector(selectAllArticles);
    const loading = useSelector(selectArticleStatus) === 'loading';
    const error = useSelector(selectArticleError);
    const articleTypeDict = useSelector(selectArticleTypeDict);
    const articleTypes = useSelector(selectAllArticleTypes);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, articleId: null, articleTitle: '' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

    const transformArticles = useCallback((articles, articleTypeDict) => {
        return articles.map((a) => {
            const id = a.idArticle || a.id || a.id_article; // adapt to possible backend naming
            const created = a.createdAt || a.creationDate || a.created_at || null;
            const updated = a.updatedAt || a.updated_at || a.lastUpdate || null;
            const version = (a.lastVersion || a.version || 1).toString();
            const createdTs = created ? Date.parse(created) : null;
            const updatedTs = updated ? Date.parse(updated) : null;
            // Set when the article was manually pulled to the top of the public
            // listings. The site orders by it as if it were the creation date.
            const pulledUpTs = a.pulledUpAt ? Date.parse(a.pulledUpAt) : null;
            const safeCreated = isNaN(createdTs) ? 0 : createdTs || 0;
            const safeUpdated = isNaN(updatedTs) ? 0 : updatedTs || 0;
            const effectiveTs = Math.max(safeCreated, safeUpdated); // greatest of creation/update

            // Get article type nickname
            const articleType = articleTypeDict[a.idArticleType];
            const typeNickname = articleType ? articleType.nickname : 'Article';

            return {
                id,
                raw: a, // keep original for edit callback if needed
                idArticleType: a.idArticleType, // used by the type filter
                type: typeNickname,
                title: a.title || '(untitled)',
                creationDate: created
                    ? new Date(created).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                      })
                    : '-',
                lastUpdate: updated
                    ? new Date(updated).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                      })
                    : '-',
                version,
                published: Boolean(a.visible),
                draft: Boolean(a.draft),
                // An article cannot be published without a cover image.
                hasCover: Boolean(a.idCoverImage || a.coverImage),
                pulledUp: Boolean(a.pulledUpAt),
                // Raw timestamps & composite for deterministic sorting
                _createdAt: createdTs,
                _updatedAt: updatedTs,
                _effectiveAt: effectiveTs,
                _pulledUpAt: isNaN(pulledUpTs) ? null : pulledUpTs,
            };
        });
    }, []);

    const [titleQuery, setTitleQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState(''); // '' = all types
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'published' | 'unpublished'

    // Debounce user input to avoid re-filtering on every keystroke
    useEffect(() => {
        const h = setTimeout(() => setDebouncedQuery(titleQuery.trim().toLowerCase()), 250);
        return () => clearTimeout(h);
    }, [titleQuery]);

    // Filter (type + status + title) + sort by effective date desc. A pulled-up
    // article sorts by when it was pulled up, mirroring the public listings so
    // the grid does not contradict what the site shows.
    const rows = useMemo(() => {
        const base = transformArticles(articles, articleTypeDict);
        const filtered = base.filter((r) => {
            if (typeFilter && String(r.idArticleType) !== String(typeFilter)) return false;
            if (statusFilter === 'published' && !r.published) return false;
            if (statusFilter === 'unpublished' && r.published) return false;
            if (debouncedQuery && !(r.title || '').toLowerCase().includes(debouncedQuery)) return false;
            return true;
        });
        const orderAt = (r) => r._pulledUpAt || r._effectiveAt;
        return filtered.slice().sort((a, b) => orderAt(b) - orderAt(a));
    }, [articles, articleTypeDict, transformArticles, debouncedQuery, typeFilter, statusFilter]);

    useEffect(() => {
        dispatch(fetchArticles());
    }, [dispatch]);

    // Show error toast when error changes, hide when error is cleared
    useEffect(() => {
        if (error) {
            setSnackbar({ open: true, message: `Error: ${error}`, severity: 'error' });
        } else {
            // Close the snackbar when error is cleared
            setSnackbar((prev) => (prev.severity === 'error' ? { ...prev, open: false } : prev));
        }
    }, [error]);

    const handleSnackbarClose = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const handleDelete = (id) => {
        const article = rows.find((row) => row.id === id);
        setDeleteDialog({
            open: true,
            articleId: id,
            articleTitle: article?.title || 'this article',
        });
    };

    const handleDeleteConfirm = async () => {
        const { articleId } = deleteDialog;
        setDeleteDialog({ open: false, articleId: null, articleTitle: '' });

        await dispatch(deleteArticle(articleId));
    };

    const handleDeleteCancel = () => {
        setDeleteDialog({ open: false, articleId: null, articleTitle: '' });
    };

    const handleTogglePublish = async (id) => {
        // Find the current article to get its current visibility state
        const article = rows.find((row) => row.id === id);
        if (!article) {
            console.error('Article not found:', id);
            return;
        }

        const newVisibleState = !article.published;

        await dispatch(
            toggleArticleVisibility({
                articleId: id,
                isVisible: newVisibleState,
            }),
        );
    };

    const handleTogglePullUp = async (id) => {
        const article = rows.find((row) => row.id === id);
        if (!article) {
            console.error('Article not found:', id);
            return;
        }

        await dispatch(
            toggleArticlePullUp({
                articleId: id,
                pulledUp: !article.pulledUp,
            }),
        );
    };

    const handleRowDoubleClick = (params) => {
        if (onEditArticle) {
            onEditArticle(params.row.raw || params.row); // pass original article if we preserved it
        }
    };

    const columns = [
        { field: 'type', headerName: 'Type', width: 120 },
        {
            field: 'title',
            headerName: 'Title',
            flex: 1,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {params.row.draft && (
                        <Chip label='DRAFT' size='small' color='warning' variant='outlined' style={{ fontSize: '0.7rem', height: '20px' }} />
                    )}
                    {params.row.pulledUp && (
                        <Chip label='TOP' size='small' color='primary' variant='outlined' style={{ fontSize: '0.7rem', height: '20px' }} />
                    )}
                    <span>{params.value}</span>
                </div>
            ),
        },
        { field: 'version', headerName: 'Version', width: 65 },
        // { field: 'views', headerName: 'Views', type: 'number', width: 60 },
        { field: 'creationDate', headerName: 'Creation', width: 140 },
        { field: 'lastUpdate', headerName: 'Last Update', width: 140 },
        { field: 'published', headerName: 'Published', type: 'boolean', width: 80 },
        {
            field: 'actions',
            headerName: '',
            width: 140,
            sortable: false,
            renderCell: (params) => (
                <>
                    <Tooltip title='Delete article'>
                        <IconButton color='secondary' onClick={() => handleDelete(params.id)}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={params.row.pulledUp ? 'Undo pull-up (back to its creation date)' : 'Pull to top of the listings'}>
                        <IconButton color={params.row.pulledUp ? 'primary' : 'default'} onClick={() => handleTogglePullUp(params.id)}>
                            <VerticalAlignTopIcon />
                        </IconButton>
                    </Tooltip>
                    {!params.row.published && !params.row.hasCover ? (
                        <Tooltip title='Can not publish without cover image'>
                            {/* span wrapper so the tooltip still shows on a disabled button */}
                            <span>
                                <IconButton color='primary' disabled>
                                    <BlockIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : (
                        <Tooltip title={params.row.published ? 'Unpublish (hide from the site)' : 'Publish'}>
                            <IconButton color='primary' onClick={() => handleTogglePublish(params.id)}>
                                {params.row.published ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                        </Tooltip>
                    )}
                </>
            ),
        },
    ];

    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

    // #region RETURN
    return (
        <div
            style={{
                height: 'calc(100vh - 300px)',
                minHeight: '300px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '8px',
                overflow: 'hidden',
            }}
        >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666', flex: '0 0 220px' }}>
                    <span>Title</span>
                    <input
                        type='text'
                        value={titleQuery}
                        onChange={(e) => setTitleQuery(e.target.value)}
                        placeholder='Search title...'
                        style={{
                            padding: '4px 8px',
                            fontSize: 12,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                        }}
                    />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666', flex: '0 0 180px' }}>
                    <span>Article Type</span>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        aria-label='Filter by article type'
                        style={{
                            padding: '4px 8px',
                            fontSize: 12,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            background: '#fff',
                        }}
                    >
                        <option value=''>-- All --</option>
                        {articleTypes.map((t) => (
                            <option key={t.idArticleType} value={t.idArticleType}>
                                {t.displayName || t.nickname || t.name || t.idArticleType}
                            </option>
                        ))}
                    </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666', flex: '0 0 150px' }}>
                    <span>Status</span>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        aria-label='Filter by publish status'
                        style={{
                            padding: '4px 8px',
                            fontSize: 12,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            background: '#fff',
                        }}
                    >
                        <option value='all'>-- All --</option>
                        <option value='published'>Published</option>
                        <option value='unpublished'>Unpublished</option>
                    </select>
                </label>
                {(debouncedQuery || typeFilter || statusFilter !== 'all') && (
                    <span style={{ fontSize: 11, color: '#666' }}>
                        {rows.length} match{rows.length === 1 ? '' : 'es'}
                    </span>
                )}
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pagination
                    paginationMode='client'
                    pageSizeOptions={[5, 10, 25, 50]}
                    disableRowSelectionOnClick
                    loading={loading}
                    onRowDoubleClick={handleRowDoubleClick}
                    getRowId={(r) => r.id}
                />
            </div>

            <DeleteConfirmDialog
                open={deleteDialog.open}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                articleTitle={deleteDialog.articleTitle}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant='filled' sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default ArticlesGrid;
